import { Component, computed, signal, inject, OnInit, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap, tap } from 'rxjs/operators';
import { SidebarComponent } from '../components/sidebar/sidebar.component';
import { HeaderComponent } from '../components/header/header.component';
import { FooterComponent } from '../components/footer/footer.component';
import { MenuGroup, PageMeta } from '../../core/models/menu-item.model';
import { User } from '../../core/models/user.model';
import { AuthService } from '../../core/services/auth.service';
import { MenuService } from '../../core/services/menu.service';
import { LoggerService } from '../../core/services/logger.service';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, SidebarComponent, HeaderComponent, FooterComponent],
  template: `
    <div class="layout-wrapper">
      <!-- Backdrop for mobile -->
      <div
        class="layout-backdrop"
        [class.layout-backdrop--visible]="mobileSidebarOpen()"
        (click)="closeMobileSidebar()"
      ></div>

      <!-- Sidebar -->
      <app-sidebar
        [collapsed]="sidebarCollapsed()"
        [menuGroups]="menuGroups()"
        [currentUser]="currentUser()"
        (toggleCollapse)="toggleSidebar()"
        (itemSelected)="handleSidebarItemSelected()"
        class="layout-sidebar"
        [class.layout-sidebar--collapsed]="sidebarCollapsed()"
        [class.layout-sidebar--mobile-open]="mobileSidebarOpen()"
      />

      <!-- Main Content Area -->
      <div class="layout-main">
        <!-- Header -->
        <app-header
          [currentUser]="currentUser()"
          [pageTitle]="pageMeta().title"
          [pageIcon]="pageMeta().icon"
          [pageDescription]="pageMeta().description"
          [pageCrumbs]="pageMeta().crumbs"
          (toggleSidebar)="handleToggleSidebar()"
          class="layout-header"
        />

        <!-- Scrollable Content -->
        <main class="layout-content">
          <router-outlet />
          <app-footer class="layout-footer" />
        </main>
      </div>
    </div>
  `,
  styleUrl: './main-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly menuService = inject(MenuService);
  private readonly logger = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  sidebarCollapsed = signal(false);
  mobileSidebarOpen = signal(false);
  menuGroups = signal<MenuGroup[]>([]);
  currentUser = computed<User | null>(() => this.authService.currentUser());
  pageMeta = signal<PageMeta>({ title: '', icon: '', crumbs: [] });

  ngOnInit(): void {
    this.listenForRouteChanges();
    this.loadInitialData();
  }

  handleToggleSidebar(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.mobileSidebarOpen.update((v) => !v);
    } else {
      this.sidebarCollapsed.update((v) => !v);
    }
  }

  toggleSidebar(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.mobileSidebarOpen.update((value) => !value);
    } else {
      this.sidebarCollapsed.update((value) => !value);
    }
  }

  closeMobileSidebar(): void {
    this.mobileSidebarOpen.set(false);
  }

  handleSidebarItemSelected(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.closeMobileSidebar();
    }
  }

  private loadInitialData(): void {
    this.authService.loadCurrentUser()
      .pipe(
        switchMap(() => this.menuService.loadMenuItems()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.menuGroups.set(this.menuService.menuGroups());
          this.pageMeta.set(this.menuService.resolvePageMeta(this.router.url));
        },
        error: (err) => {
          this.logger.error('MainLayout: Failed to load initial data', err);
        },
      });
  }

  private listenForRouteChanges(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        const navigation = event as NavigationEnd;
        this.pageMeta.set(this.menuService.resolvePageMeta(navigation.urlAfterRedirects));
      });
  }
}
